import type { CampaignUploadFailureReason } from '@/domain/campaign-ports'

/**
 * User-facing copy for every stable upload failure reason. A complete `Record`
 * keyed by the domain union, so a reason added upstream becomes a compile error
 * rather than falling through to unrelated generic copy — there is deliberately
 * no `default`, no `??` fallback, and no catch-all entry.
 *
 * Each string is remediation-oriented and carries no HTTP status, transport
 * detail, exception text, or internal upload-step name. The same map serves an
 * immediate selection rejection and an execution-reported failure, so a rejected
 * type reads identically either way.
 *
 * Association failure is NOT part of this vocabulary — it is its own retryable
 * outcome with its own presentation.
 *
 * Campaign-local: no shared cross-feature error taxonomy is created here.
 */
export const CAMPAIGN_UPLOAD_FAILURE_COPY: Record<CampaignUploadFailureReason, string> = {
	'invalid-file-type': 'That File type is not supported. Choose a File in one of the accepted formats.',
	'file-too-large': 'That File is larger than the maximum permitted size. Choose a smaller File.',
	'empty-file': 'That File is empty. Choose a File that has content in it.',
	'expired-upload-url': 'This upload took too long to start. Upload the File again.',
	// The repository's established wording for a failed transfer, kept verbatim —
	// it is already distinct from every other entry, so the collapse this map
	// removes is fixed without churning copy that never needed to change.
	'transfer-failed': 'Upload failed. Choose a File and try again.',
	'content-rejected': 'The File contents were rejected as unsafe or unreadable. Choose a different File.',
	forbidden: 'You do not have permission to upload Files to this Campaign.',
	unavailable: 'Uploading is temporarily unavailable. Wait a moment and try again.',
	failure: 'The upload could not be completed. Try uploading the File again.',
}

/**
 * Whether a failure reason describes the staged File itself, rather than the
 * environment. Only these are associated with the file input as a validation
 * error; a permission or availability failure stands alone instead.
 */
export function isFileScopedUploadFailure(reason: CampaignUploadFailureReason): boolean {
	return (
		reason === 'invalid-file-type' ||
		reason === 'file-too-large' ||
		reason === 'empty-file' ||
		reason === 'content-rejected'
	)
}
